import ProfileAvatar from '../../profile/ProfileAvatar'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useRef, useEffect, useState } from 'react'
import { Popover } from '@mantine/core'

export default function ChatBubble({
    actor,
    text,
    isSelf,
    isEdited = false,
    messages = null,
    onMessageClick = null,
    selectedMessageId = null,
    removingMessages = null,
    editingMessageId = null,
    editingText = '',
    onEditingTextChange = null,
    onSaveEdit = null,
    onCancelEdit = null,
    onRemoveReaction = null,
    onAddReaction = null,
    currentUserId = null
}) {
    // If messages array is provided, use it; otherwise use single text for backwards compatibility
    const messageList = messages || [{ text, isEdited, id: null }]
    const editTextareaRef = useRef(null)
    const [hoveredReaction, setHoveredReaction] = useState(null) // Track which reaction is hovered

    // Focus and set cursor to end when editing starts
    useEffect(() => {
        if (editingMessageId && editTextareaRef.current) {
            const textarea = editTextareaRef.current
            textarea.focus()
            // Set cursor to end of text
            const length = textarea.value.length
            textarea.setSelectionRange(length, length)
        }
    }, [editingMessageId])

    return (
        <div className={`ChatMessageRow ${isSelf ? 'Self' : 'Other'}`}>
            <div className={`ChatBubble ${isSelf ? 'Self' : 'Other'}`}>
                <div className="ChatBubbleContent">
                    {!isSelf && <ProfileAvatar user={actor} size={32} />}
                    <div className="ChatBubbleTextContainer">
                        <div className="ChatBubbleHeader">{actor.username}</div>
                        {messageList.map((msg, idx) => {
                            const isSelected = msg.id && selectedMessageId === msg.id
                            const canClick = msg.id && onMessageClick
                            const isRemoving = msg.id && removingMessages && removingMessages.has(msg.id)
                            const isEditing = msg.id && editingMessageId === msg.id

                            return (
                                <div key={msg.id || idx} style={{ marginTop: idx > 0 ? '8px' : '0' }}>
                                    <div
                                        className="ChatBubbleText"
                                        style={{
                                            cursor: canClick && !isEditing ? 'pointer' : 'default',
                                            padding: '6px 8px',
                                            borderRadius: '6px',
                                            backgroundColor: isSelected || isEditing ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
                                            outline: isSelected || isEditing ? '2px solid rgba(59, 130, 246, 0.5)' : 'none',
                                            transition: 'background-color 0.15s, outline 0.15s, opacity 0.3s ease-out, transform 0.3s ease-out, max-height 0.3s ease-out',
                                            opacity: isRemoving ? 0 : 1,
                                            transform: isRemoving ? 'translateX(20px)' : 'translateX(0)',
                                            maxHeight: isRemoving ? '0px' : 'none',
                                            overflow: isRemoving ? 'hidden' : 'visible',
                                        }}
                                        onClick={canClick && !isEditing ? (e) => {
                                            e.stopPropagation()
                                            onMessageClick(e, msg.id)
                                        } : undefined}
                                        onMouseEnter={canClick && !isEditing ? (e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'
                                            }
                                        } : undefined}
                                        onMouseLeave={canClick && !isEditing ? (e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = 'transparent'
                                            }
                                        } : undefined}
                                    >
                                        {isEditing ? (
                                            <textarea
                                                ref={editTextareaRef}
                                                value={editingText}
                                                onChange={(e) => onEditingTextChange && onEditingTextChange(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault()
                                                        onSaveEdit && onSaveEdit(msg.id)
                                                    } else if (e.key === 'Escape') {
                                                        e.preventDefault()
                                                        onCancelEdit && onCancelEdit()
                                                    }
                                                }}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '60px',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                                    color: '#fff',
                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                    borderRadius: '4px',
                                                    padding: '8px',
                                                    fontSize: '14px',
                                                    fontFamily: 'inherit',
                                                    resize: 'vertical',
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkGfm]}
                                                    components={{
                                                        // Prevent headings (use normal text instead)
                                                        h1: 'p',
                                                        h2: 'p',
                                                        h3: 'p',
                                                        h4: 'p',
                                                        h5: 'p',
                                                        h6: 'p',
                                                        // Open links in new tab
                                                        a: ({ node, children, ...props }) => (
                                                            <a {...props} target="_blank" rel="noopener noreferrer">
                                                                {children}
                                                            </a>
                                                        ),
                                                        // Prevent images
                                                        // eslint-disable-next-line no-unused-vars
                                                        img: () => null,
                                                    }}
                                                    allowedElements={[
                                                        'p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'a', 'ul', 'ol', 'li',
                                                        'table', 'thead', 'tbody', 'tr', 'th', 'td'
                                                    ]}
                                                    unwrapDisallowed={true}
                                                >
                                                    {msg.text}
                                                </ReactMarkdown>
                                                {msg.isEdited && (
                                                    <span className="ChatBubbleEdited" style={{ opacity: 0.7, fontSize: '0.8em' }}>
                                                        (edited)
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {isEditing && (
                                        <div style={{
                                            display: 'flex',
                                            gap: '8px',
                                            marginTop: '8px',
                                            justifyContent: 'flex-end'
                                        }}>
                                            <button
                                                onClick={() => onCancelEdit && onCancelEdit()}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    padding: '6px 12px',
                                                    backgroundColor: '#f06595',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#e64980'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = '#f06595'}
                                            >
                                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                                                Cancel
                                            </button>
                                            <button
                                                onClick={() => onSaveEdit && onSaveEdit(msg.id)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    padding: '6px 12px',
                                                    backgroundColor: '#5c7cfa',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#4c6ef5'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = '#5c7cfa'}
                                            >
                                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>check</span>
                                                Save
                                            </button>
                                        </div>
                                    )}
                                    {msg.reactions && msg.reactions.length > 0 && (
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '6px',
                                            marginTop: '6px'
                                        }}>
                                            {msg.reactions.map((reaction, reactionIdx) => {
                                                const userReacted = reaction.users.some(u => u.userId === currentUserId)
                                                const userReaction = reaction.users.find(u => u.userId === currentUserId)
                                                const reactionKey = `${msg.id}-${reactionIdx}`
                                                const isHovered = hoveredReaction === reactionKey

                                                return (
                                                    <Popover
                                                        key={reactionIdx}
                                                        position="top"
                                                        withArrow
                                                        shadow="md"
                                                        opened={isHovered}
                                                    >
                                                        <Popover.Target>
                                                            <div
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    if (userReacted && userReaction && onRemoveReaction) {
                                                                        // User has reacted, remove their reaction
                                                                        onRemoveReaction(userReaction.reactionId)
                                                                    } else if (!userReacted && onAddReaction) {
                                                                        // User hasn't reacted, add their reaction
                                                                        onAddReaction(msg.id, reaction.emoji)
                                                                    }
                                                                }}
                                                                onMouseEnter={() => setHoveredReaction(reactionKey)}
                                                                onMouseLeave={() => setHoveredReaction(null)}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    padding: '4px 8px',
                                                                    backgroundColor: userReacted ? 'rgba(77, 171, 247, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                                                                    border: userReacted ? '1px solid rgba(77, 171, 247, 0.6)' : '1px solid rgba(255, 255, 255, 0.2)',
                                                                    borderRadius: '12px',
                                                                    fontSize: '13px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s',
                                                                    transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                                                                }}
                                                            >
                                                                <span>{reaction.emoji}</span>
                                                                <span style={{ fontSize: '11px', color: '#aaa' }}>
                                                                    {reaction.count}
                                                                </span>
                                                            </div>
                                                        </Popover.Target>
                                                        <Popover.Dropdown
                                                            style={{
                                                                backgroundColor: '#fff',
                                                                border: '1px solid #e0e0e0',
                                                            }}
                                                        >
                                                            <div style={{ padding: '4px' }}>
                                                                {reaction.users.map((user, userIdx) => (
                                                                    <div key={userIdx} style={{
                                                                        padding: '4px 8px',
                                                                        color: '#333',
                                                                        fontSize: '13px'
                                                                    }}>
                                                                        {user.username}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </Popover.Dropdown>
                                                    </Popover>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                    {isSelf && <ProfileAvatar user={actor} size={32} />}
                </div>
            </div>
        </div>
    )
}

